import React, { useState, useMemo } from 'react';
import {
  AlertCircle, Calculator, TrendingUp, X, RefreshCw,
  Search, CheckCircle2, ClipboardCheck,
} from 'lucide-react';
import { useGetMatieresQuery } from '../../services/matieresApi';
import { useGetClassesQuery } from '../../services/polesOptionsApi';
import {
  useDetecterAnomaliesExamensQuery,
  useArrondirNotesExamensMutation,
  usePlafondPlancherExamensMutation,
} from '../../services/notesExamensApi';
import {
  useDetecterAnomaliesDevoirsQuery,
  useArrondirNotesDevoirsMutation,
  usePlafondPlancherDevoirsMutation,
} from '../../services/notesDevoirsApi';
import { formatNote } from '../../utils/formatNote';

type Variante = 'devoirs' | 'examens';

interface Props {
  variante: Variante;
  anneeAcademique: string;
}

type Action = 'anomalies' | 'arrondi' | 'plafond';

const OutilsAvances: React.FC<Props> = ({ variante, anneeAcademique }) => {
  const [action, setAction] = useState<Action>('anomalies');
  const [semestre, setSemestre] = useState<'S1' | 'S2'>('S1');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  return (
    <div className="space-y-4">
      {msg && (
        <div className={`p-3 rounded-lg text-sm flex justify-between items-start ${
          msg.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)}><X size={14} /></button>
        </div>
      )}

      {/* Tabs actions */}
      <div className="flex items-center gap-2 flex-wrap">
        <TabButton
          active={action === 'anomalies'}
          onClick={() => setAction('anomalies')}
          icon={<AlertCircle size={14} />}
          label="Anomalies"
          color="red"
        />
        <TabButton
          active={action === 'arrondi'}
          onClick={() => setAction('arrondi')}
          icon={<Calculator size={14} />}
          label="Arrondi"
          color="blue"
        />
        <TabButton
          active={action === 'plafond'}
          onClick={() => setAction('plafond')}
          icon={<TrendingUp size={14} />}
          label="Plancher / Plafond"
          color="violet"
        />

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">Semestre :</span>
          <select
            value={semestre}
            onChange={e => setSemestre(e.target.value as 'S1' | 'S2')}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
          >
            <option value="S1">S1</option>
            <option value="S2">S2</option>
          </select>
        </div>
      </div>

      {action === 'anomalies' && (
        <AnomaliesPanel
          variante={variante}
          anneeAcademique={anneeAcademique}
          semestre={semestre}
        />
      )}
      {action === 'arrondi' && (
        <ArrondiPanel
          variante={variante}
          anneeAcademique={anneeAcademique}
          semestre={semestre}
          onSuccess={(txt) => setMsg({ type: 'success', text: txt })}
          onError={(txt) => setMsg({ type: 'error', text: txt })}
        />
      )}
      {action === 'plafond' && (
        <PlafondPanel
          variante={variante}
          anneeAcademique={anneeAcademique}
          semestre={semestre}
          onSuccess={(txt) => setMsg({ type: 'success', text: txt })}
          onError={(txt) => setMsg({ type: 'error', text: txt })}
        />
      )}
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean; onClick: () => void;
  icon: React.ReactNode; label: string;
  color: 'red' | 'blue' | 'violet';
}> = ({ active, onClick, icon, label, color }) => {
  const cls = active
    ? {
        red: 'bg-red-600 text-white',
        blue: 'bg-blue-600 text-white',
        violet: 'bg-violet-600 text-white',
      }[color]
    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50';
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium ${cls}`}
    >
      {icon} {label}
    </button>
  );
};

// ─── Panel 1 : Anomalies ───────────────────────────────────────────────────
const AnomaliesPanel: React.FC<{
  variante: Variante; anneeAcademique: string; semestre: 'S1' | 'S2';
}> = ({ variante, anneeAcademique, semestre }) => {
  const [niveau, setNiveau] = useState('');
  const [classe, setClasse] = useState('');

  const queryParams = { annee_academique: anneeAcademique, semestre, niveau: niveau || undefined, classe: classe || undefined };
  const devoirsQ = useDetecterAnomaliesDevoirsQuery(queryParams, { skip: variante !== 'devoirs' });
  const examensQ = useDetecterAnomaliesExamensQuery(queryParams, { skip: variante !== 'examens' });
  const { data, isLoading, refetch } = variante === 'devoirs' ? devoirsQ : examensQ;

  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const niveaux = ['L1', 'L2', 'L3', 'M1', 'M2'];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400">Niveau</label>
          <select value={niveau} onChange={e => setNiveau(e.target.value)}
            className="block px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">Tous</option>
            {niveaux.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 dark:text-gray-400">Classe</label>
          <select value={classe} onChange={e => setClasse(e.target.value)}
            className="block px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
            <option value="">Toutes</option>
            {(classes as any[]).filter((c: any) => !niveau || c.niveau === niveau).map((c: any) =>
              <option key={c.id} value={c.code}>{c.code}</option>
            )}
          </select>
        </div>
        <button onClick={() => refetch()} className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200">
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
        </button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard color="red" count={data.hors_plage.length} label="Hors plage [0-20]" />
            <StatCard color="orange" count={data.incoherentes.length} label="Incohérentes (absent+note)" />
            <StatCard color="yellow" count={data.incompletes.length} label="Incomplètes" />
          </div>

          {data.total_anomalies === 0 ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 size={16} /> Aucune anomalie détectée.
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Étudiant</th>
                      <th className="px-3 py-2 text-left">Classe</th>
                      <th className="px-3 py-2 text-left">Matière</th>
                      <th className="px-3 py-2 text-center">Notes</th>
                      <th className="px-3 py-2 text-left">Problème</th>
                    </tr>
                  </thead>
                  <tbody>
                    {renderAnomalieRows(data.hors_plage, 'hors_plage', variante)}
                    {renderAnomalieRows(data.incoherentes, 'incoherentes', variante)}
                    {renderAnomalieRows(data.incompletes, 'incompletes', variante)}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function renderAnomalieRows(items: any[], type: string, variante: Variante) {
  const typeLabel = {
    hors_plage: { label: 'Hors plage', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
    incoherentes: { label: 'Incohérente', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
    incompletes: { label: 'Incomplète', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' },
  }[type] || { label: type, cls: 'bg-gray-100 text-gray-700' };

  return items.map((a: any) => (
    <tr key={`${type}-${a.id}`} className="border-b border-gray-100 dark:border-gray-700/50">
      <td className="px-3 py-2">
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${typeLabel.cls}`}>
          {typeLabel.label}
        </span>
      </td>
      <td className="px-3 py-2">
        <div className="text-xs font-mono text-gray-500">{a.matricule}</div>
        <div className="text-sm">{a.nom}</div>
      </td>
      <td className="px-3 py-2 text-xs font-mono">{a.classe}</td>
      <td className="px-3 py-2 text-xs">
        <div className="font-mono text-gray-500">{a.matiere_code}</div>
        <div className="text-sm">{a.matiere}</div>
      </td>
      <td className="px-3 py-2 text-center text-xs">
        {variante === 'devoirs' ? (
          <span>
            D1: {formatNote(a.note1)}{a.absent1 ? ' (abs)' : ''} · D2: {formatNote(a.note2)}{a.absent2 ? ' (abs)' : ''}
          </span>
        ) : (
          <span>
            {formatNote(a.note_examen)}{a.absent ? ' (abs)' : ''}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
        {a.probleme || (a.problemes || []).join(', ')}
      </td>
    </tr>
  ));
}

// ─── Panel 2 : Arrondi ─────────────────────────────────────────────────────
const ArrondiPanel: React.FC<{
  variante: Variante; anneeAcademique: string; semestre: 'S1' | 'S2';
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ variante, anneeAcademique, semestre, onSuccess, onError }) => {
  const [form, setForm] = useState({
    matiere_id: '', classes: [] as string[],
    palier: 0.5 as 0.25 | 0.5 | 1,
    sens: 'proche' as 'proche' | 'sup' | 'inf',
    commentaire: '',
  });
  const [arrondirDev] = useArrondirNotesDevoirsMutation();
  const [arrondirEx] = useArrondirNotesExamensMutation();

  const submit = async () => {
    if (!form.matiere_id || form.classes.length === 0) {
      onError('Sélectionnez une matière et au moins une classe');
      return;
    }
    const payload = {
      matiere_id: Number(form.matiere_id),
      classes: form.classes,
      annee_academique: anneeAcademique,
      semestre,
      palier: form.palier,
      sens: form.sens,
      commentaire: form.commentaire,
    };
    try {
      const res: any = variante === 'devoirs'
        ? await arrondirDev(payload as any).unwrap()
        : await arrondirEx(payload as any).unwrap();
      onSuccess(`${res.nb_modifies} note(s) arrondie(s), ${res.nb_inchangees} inchangée(s).`);
    } catch (err: any) {
      onError(err?.data?.error || 'Erreur');
    }
  };

  return (
    <ActionForm
      submitLabel="Arrondir les notes"
      onSubmit={submit}
      form={form}
      setForm={setForm}
      variante={variante}
      extra={(
        <>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Palier</label>
            <select value={form.palier}
              onChange={e => setForm(f => ({ ...f, palier: parseFloat(e.target.value) as any }))}
              className="block w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
              <option value={0.25}>0,25</option>
              <option value={0.5}>0,5</option>
              <option value={1}>1 (entier)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Sens</label>
            <select value={form.sens}
              onChange={e => setForm(f => ({ ...f, sens: e.target.value as any }))}
              className="block w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
              <option value="proche">Au plus proche</option>
              <option value="sup">Supérieur (↑)</option>
              <option value="inf">Inférieur (↓)</option>
            </select>
          </div>
        </>
      )}
    />
  );
};

// ─── Panel 3 : Plancher / Plafond ──────────────────────────────────────────
const PlafondPanel: React.FC<{
  variante: Variante; anneeAcademique: string; semestre: 'S1' | 'S2';
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ variante, anneeAcademique, semestre, onSuccess, onError }) => {
  const [form, setForm] = useState({
    matiere_id: '', classes: [] as string[],
    plancher: '', plafond: '',
    commentaire: '',
  });
  const [applyDev] = usePlafondPlancherDevoirsMutation();
  const [applyEx] = usePlafondPlancherExamensMutation();

  const submit = async () => {
    if (!form.matiere_id || form.classes.length === 0) {
      onError('Sélectionnez une matière et au moins une classe');
      return;
    }
    if (!form.plancher && !form.plafond) {
      onError('Renseignez au moins un plancher ou un plafond');
      return;
    }
    const payload = {
      matiere_id: Number(form.matiere_id),
      classes: form.classes,
      annee_academique: anneeAcademique,
      semestre,
      plancher: form.plancher ? Number(form.plancher) : null,
      plafond: form.plafond ? Number(form.plafond) : null,
      commentaire: form.commentaire,
    };
    try {
      const res: any = variante === 'devoirs'
        ? await applyDev(payload as any).unwrap()
        : await applyEx(payload as any).unwrap();
      onSuccess(`${res.nb_relevees} relevée(s), ${res.nb_abaissees} abaissée(s), ${res.nb_inchangees} inchangée(s).`);
    } catch (err: any) {
      onError(err?.data?.error || 'Erreur');
    }
  };

  return (
    <ActionForm
      submitLabel="Appliquer plancher/plafond"
      onSubmit={submit}
      form={form}
      setForm={setForm}
      variante={variante}
      extra={(
        <>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Plancher (min)</label>
            <input type="number" step="0.25" min="0" max="20"
              value={form.plancher}
              onChange={e => setForm(f => ({ ...f, plancher: e.target.value }))}
              placeholder="ex: 8"
              className="block w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 dark:text-gray-400">Plafond (max)</label>
            <input type="number" step="0.25" min="0" max="20"
              value={form.plafond}
              onChange={e => setForm(f => ({ ...f, plafond: e.target.value }))}
              placeholder="ex: 18"
              className="block w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
            />
          </div>
        </>
      )}
    />
  );
};

// ─── Formulaire partagé (matière + classes + commentaire + champs extra) ──
const ActionForm: React.FC<{
  submitLabel: string;
  onSubmit: () => void | Promise<void>;
  form: any;
  setForm: (f: any) => void;
  variante: Variante;
  extra?: React.ReactNode;
}> = ({ submitLabel, onSubmit, form, setForm, extra }) => {
  const { data: matieres = [] } = useGetMatieresQuery();
  const { data: classes = [] } = useGetClassesQuery({ actif: true });
  const [matSearch, setMatSearch] = useState('');
  const matieresFiltrees = useMemo(() => {
    if (matSearch.length < 2) return [];
    const q = matSearch.toLowerCase();
    return (matieres as any[]).filter((m: any) =>
      m.nom_matiere?.toLowerCase().includes(q) || m.code_matiere?.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [matieres, matSearch]);

  const toggleClasse = (code: string) => {
    setForm({
      ...form,
      classes: form.classes.includes(code) ? form.classes.filter((c: string) => c !== code) : [...form.classes, code],
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
      <div>
        <label className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-1">
          <Search size={12} /> Matière
        </label>
        <input
          value={matSearch}
          onChange={e => { setMatSearch(e.target.value); setForm({ ...form, matiere_id: '' }); }}
          placeholder="Rechercher par nom ou code..."
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
        {matSearch.length >= 2 && !form.matiere_id && (
          <div className="mt-1 border border-gray-200 dark:border-gray-600 rounded max-h-40 overflow-y-auto">
            {matieresFiltrees.map((m: any) => (
              <button key={m.id} type="button"
                onClick={() => { setForm({ ...form, matiere_id: String(m.id) }); setMatSearch(`${m.code_matiere} · ${m.nom_matiere}`); }}
                className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
              >
                <span className="font-mono text-gray-500">{m.code_matiere}</span> · {m.nom_matiere}
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Classes</label>
        <div className="flex flex-wrap gap-1 max-h-28 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded p-2">
          {(classes as any[]).map((c: any) => (
            <button key={c.id} type="button" onClick={() => toggleClasse(c.code)}
              className={`px-2 py-1 text-xs rounded ${
                form.classes.includes(c.code)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              {c.code}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {extra}
      </div>

      <div>
        <label className="text-xs text-gray-500 dark:text-gray-400">Commentaire (facultatif)</label>
        <input
          value={form.commentaire}
          onChange={e => setForm({ ...form, commentaire: e.target.value })}
          placeholder="Raison de l'action..."
          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
        />
      </div>

      <div className="flex justify-end">
        <button onClick={onSubmit}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg flex items-center gap-1">
          <ClipboardCheck size={14} /> {submitLabel}
        </button>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ color: 'red' | 'orange' | 'yellow'; count: number; label: string }> = ({ color, count, label }) => {
  const cls = {
    red: 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300',
    orange: 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-300',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300',
  }[color];
  return (
    <div className={`p-3 border rounded-lg ${cls}`}>
      <div className="text-2xl font-bold">{count}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
};

export default OutilsAvances;
